import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { AuthService } from './auth-service';
import { environment } from '../../../environments/environment';
import { ApplicationType, AutoAssessmentDto } from '../models/auto-assessment.model';
import { CompanyBackgroundDto } from '../models/company-background.model';
import { ManagementProfilesDto } from '../models/management-profile.model';
import { FundTypeService } from './fund-type.service';

@Injectable({
    providedIn: 'root',
})
export class ApplicationService {
    private apiUrl = `${environment.azure.apiBaseUrl}/v1/Applications`;
    private readonly defaultApplicationType: ApplicationType = 'Isibaya';

    constructor(private http: HttpClient, private authService: AuthService, private fundTypeService: FundTypeService) {}

    getActiveApplicationByUser(applicationType: ApplicationType | null = this.fundTypeService.selectedApplicationType): Observable<any> {
        const resolvedApplicationType = applicationType ?? this.defaultApplicationType;
        return this.http
            .get<any>(`${this.apiUrl}/GetActiveApplicationByUser`, {
                ...this.getHeaders(),
                params: {
                    applicationType: resolvedApplicationType,
                },
            })
            .pipe(map((activeApplication) => this.filterActiveApplicationByType(activeApplication)));
    }

        getSubmittedApplicationsByUser(filterByApplicationType: boolean = true): Observable<any> {
        return this.http
            .get<any>(`${this.apiUrl}/GetSubmittedApplicationsByUser`, this.getHeaders())
            .pipe(map((applications) => filterByApplicationType ? this.filterApplicationsByType(applications) : applications));
    }

        getApplicationsByUser(filterByApplicationType: boolean = true): Observable<any> {
        return this.http
            .get<any>(`${this.apiUrl}/GetApplicationsByUser`, this.getHeaders())
            .pipe(map((applications) => filterByApplicationType ? this.filterApplicationsByType(applications) : applications));
    }

    saveAutoAssessment(autoAssessmentData: AutoAssessmentDto): Observable<AutoAssessmentDto> {
        return this.http.post<AutoAssessmentDto>(`${this.apiUrl}/SaveAutoAssessment`, this.withApplicationType(autoAssessmentData), this.getHeaders());
    }

    submitAutoAssessment(autoAssessmentData: AutoAssessmentDto): Observable<AutoAssessmentDto> {
        return this.http.post<AutoAssessmentDto>(`${this.apiUrl}/SubmitAutoAssessment`, this.withApplicationType(autoAssessmentData), this.getHeaders());
    }

    getInformationRequestsByUser(): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/GetInformationRequestsByUser`, this.getHeaders());
    }

    saveCompanyBackground(applicationId: number, companyBackgroundData: CompanyBackgroundDto): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/${applicationId}/SaveCompanyBackground`, this.withApplicationType(companyBackgroundData), this.getHeaders());
    }

    submitCompanyBackground(applicationId: number, companyBackgroundData: CompanyBackgroundDto): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/${applicationId}/SubmitCompanyBackground`, this.withApplicationType(companyBackgroundData), this.getHeaders());
    }

    saveApplicationDetails(applicationId: number, applicationDetailsData: any): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/${applicationId}/SaveApplicationDetails`, this.withApplicationType(applicationDetailsData), this.getHeaders());
    }

    submitApplicationDetails(applicationId: number, applicationDetailsData: any): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/${applicationId}/SubmitApplicationDetails`, this.withApplicationType(applicationDetailsData), this.getHeaders());
    }

    saveManagementProfiles(applicationId: number, managementProfilesData: ManagementProfilesDto): Observable<ManagementProfilesDto> {
        return this.http.post<ManagementProfilesDto>(`${this.apiUrl}/${applicationId}/SaveManagementProfiles`, this.withApplicationType(managementProfilesData), this.getHeaders());
    }

    submitManagementProfiles(applicationId: number, managementProfilesData: ManagementProfilesDto): Observable<ManagementProfilesDto> {
        return this.http.post<ManagementProfilesDto>(`${this.apiUrl}/${applicationId}/SubmitManagementProfiles`, this.withApplicationType(managementProfilesData), this.getHeaders());
    }

    getSupportingDocuments(applicationId: number): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/${applicationId}/Documents`, this.getHeaders());
    }

    uploadSupportingDocuments(applicationId: number, category: string, files: File[]): Observable<any> {
        var formData = new FormData();
        files.forEach(file => {
            formData.append('files', file, file.name);
        });
        
        const token = this.authService.getToken();
        const headers = {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        };
        
        return this.http.post<any>(`${this.apiUrl}/${applicationId}/UploadSupportingDocuments?category=${category}`, formData, headers);
    }

    // final application submission with consent
    submitApplication(applicationId: number): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/${applicationId}/SubmitApplication`, null, this.getHeaders());
    }

    assessEligibilityForFunding(autoAssessment: AutoAssessmentDto): string[] {
        const factorsAffectingApplication: string[] = [];
        const fundingAmountRequested = autoAssessment.fundingAmountRequested ?? 0;
        const applicationType = autoAssessment.applicationType ?? this.fundTypeService.selectedApplicationType ?? 'Isibaya';
        const minimumFundingThreshold = applicationType === 'Early Stage' ? 50000000 : 100000000;

        // Check minimum requested funding threshold by application type.
        if (fundingAmountRequested < minimumFundingThreshold) {
            const minimumLabel = minimumFundingThreshold === 50000000 ? 'R50m' : 'R100m';
            factorsAffectingApplication.push(`Funding amount requested is below the minimum threshold of ${minimumLabel}`);
        }

        // check if own contribution is at least 10% of requested amount
        // if ((autoAssessment.fundingAmountOwn ?? 0) < 0.1 * fundingAmountRequested) {
        //     factorsAffectingApplication.push('Own contribution is less than 10% of requested amount');
        // }

        // check if external funding raised is at least 20% of requested amount
        // if ((autoAssessment.fundingAmountRaised ?? 0) < 1.2 * fundingAmountRequested) {
        //     factorsAffectingApplication.push('External funding raised is less than 20% of requested amount');
        // }

        // check if tax certificate is not expired already
        if (autoAssessment.taxCertificateIssued) {
            const taxCertificateIssued = new Date(autoAssessment.taxCertificateIssued);
            const currentDate = new Date();

            if (!Number.isNaN(taxCertificateIssued.getTime())) {
                const diffTime = Math.abs(currentDate.getTime() - taxCertificateIssued.getTime());
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                if (diffDays > 365) {
                    factorsAffectingApplication.push('Tax certificate is expired');
                }
            }
        }

        return factorsAffectingApplication;
    }

    private getHeaders() {
        // Get the token from localStorage or your auth service
        const token = this.authService.getToken();
        return {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        };
    }

    private withApplicationType<T>(payload: T): T {
        if (!payload || typeof payload !== 'object') {
            return payload;
        }

        const existingApplicationType = (payload as any).applicationType;
        if (existingApplicationType) {
            return payload;
        }

        const selectedApplicationType = this.fundTypeService.selectedApplicationType;
        const applicationType = selectedApplicationType ?? 'Isibaya';

        return {
            ...(payload as any),
            applicationType
        } as T;
    }

    private filterActiveApplicationByType(activeApplication: any): any {
        if (!activeApplication) {
            return activeApplication;
        }

        const selectedApplicationType = this.fundTypeService.selectedApplicationType ?? this.defaultApplicationType;

        if (Array.isArray(activeApplication)) {
            return activeApplication.find((application) => this.resolveApplicationType(application) === selectedApplicationType) ?? null;
        }

        return this.resolveApplicationType(activeApplication) === selectedApplicationType
            ? activeApplication
            : null;
    }

    private filterApplicationsByType(applications: any): any {
        if (!applications) {
            return applications;
        }

        const selectedApplicationType = this.fundTypeService.selectedApplicationType ?? this.defaultApplicationType;

        if (Array.isArray(applications)) {
            return applications.filter((application) => this.resolveApplicationType(application) === selectedApplicationType);
        }

        if (Array.isArray(applications?.applications)) {
            return {
                ...applications,
                applications: applications.applications.filter((application: any) => this.resolveApplicationType(application) === selectedApplicationType)
            };
        }

        return this.resolveApplicationType(applications) === selectedApplicationType
            ? applications
            : null;
    }

    private resolveApplicationType(application: any): ApplicationType {
        const rawApplicationType =
            application?.applicationType
            ?? application?.autoAssessment?.applicationType
            ?? application?.companyBackground?.applicationType
            ?? this.defaultApplicationType;

        switch (rawApplicationType) {
            case 'Early Stage':
                return 'Early Stage';
            case 'Fund of Funds':
            case 'FundOfFunds':
                return 'Fund of Funds';
            default:
                return 'Isibaya';
        }
    }
}

